import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import ProfilePicture from "./ProfilePicture";
import DropdownMenu from "./shared/DropdownMenu";
import DropdownMenuItem from "./shared/DropdownItem";
import { ChevronsUpDown, LogOut, Settings } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { exitSandboxSession, logout } from "../store/slices/appAuthSlice";
import { exitSandbox, isSandboxMode } from "../lib/sandbox";
import type { AppDispatch, RootState } from "../store/store";

interface UserDropdownMenuProps {
  /** Avatar only, for the collapsed rail. Otherwise a labelled row. */
  collapsed?: boolean;
  className?: string;
  /** Lets the rail hold its hover peek open while the menu is showing. */
  onOpenChange?: (open: boolean) => void;
}

export default function UserDropdownMenu({
  collapsed = false,
  className = "",
  onOpenChange,
}: UserDropdownMenuProps) {
  const auth = useAuth();
  const user = useSelector((s: RootState) => s.appAuthReducer.user);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const handleLogout = () => {
    // Clear local Redux state and cached token immediately.
    dispatch(logout());
    // Redirect to ZITADEL's end_session endpoint so the server-side session is
    // invalidated. ZITADEL will redirect back to post_logout_redirect_uri
    // (configured in main.tsx) after the logout is complete, requiring the
    // user to re-enter their credentials on the next login.
    void auth.signoutRedirect();
  };

  // Sandbox sessions have no profile to manage and no Zitadel session to end:
  // offer a single exit back to the commercial website.
  const handleExitSandbox = () => {
    exitSandbox();
    dispatch(exitSandboxSession());
    window.location.href = "https://insaion.com";
  };

  const label = isSandboxMode()
    ? "Sandbox session"
    : user?.name?.trim() || user?.email || "Account";

  // The rail is narrow, and a given name identifies the account well enough.
  // The full value stays on the tooltip for anyone who needs to disambiguate.
  const shortLabel = isSandboxMode()
    ? "Sandbox"
    : user?.name?.trim().split(/\s+/)[0] ||
      user?.email?.split("@")[0] ||
      "Account";

  // The same row in both states, so collapsing the rail only takes the name
  // and the chevron away — the avatar itself never moves. Like `RAIL_ROW`
  // above it, its padding does not change with the rail's width: 8px here on
  // top of the footer's own 8px holds the 28px avatar on the same centre line
  // as the 20px icons, and within a pixel of centre at the collapsed width.
  const trigger = (
    <button
      type="button"
      title={label}
      className="flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-neutral-800"
    >
      {/* `max-w-none` over Tailwind's preflight: at the collapsed width the
          button's content box is narrower than the avatar, and the default
          `max-width: 100%` on an image would squeeze it down to fit rather
          than let it overhang. `shrink-0` alone does not cover that. */}
      <ProfilePicture
        file={null}
        className="h-7 w-7 max-w-none shrink-0 rounded-full"
      />
      <span
        className={
          collapsed
            ? "sr-only"
            : "min-w-0 flex-1 truncate text-sm text-neutral-200"
        }
      >
        {shortLabel}
      </span>
      {!collapsed && (
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-neutral-500" />
      )}
    </button>
  );

  if (isSandboxMode()) {
    return (
      <DropdownMenu
        className={className}
        onOpenChange={onOpenChange}
        trigger={trigger}
      >
        <DropdownMenuItem onClick={handleExitSandbox}>
          <LogOut size={16} className="mr-3 text-neutral-400" />
          <span>Exit demo</span>
        </DropdownMenuItem>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu
      className={className}
      onOpenChange={onOpenChange}
      trigger={trigger}
    >
      <DropdownMenuItem onClick={() => navigate("/settings")}>
        <Settings size={16} className="mr-3 text-neutral-400" />
        <span>Settings</span>
      </DropdownMenuItem>
      <hr className="border-neutral-700 my-1" />
      <DropdownMenuItem onClick={handleLogout}>
        <LogOut size={16} className="mr-3 text-neutral-400" />
        <span>Logout</span>
      </DropdownMenuItem>
    </DropdownMenu>
  );
}
